import type { EventCategory, EventSeverity, ParsedEvent, Upload } from "../types/domain";

export type TimelineFilter =
  | "ALL"
  | "PROBLEMS"
  | "WATERING"
  | "ZONES"
  | "PROGRAMS"
  | "TWO_WIRE"
  | "BASEMANAGER"
  | "FLOW"
  | "MOISTURE"
  | "CONFIGURATION"
  | "SYSTEM"
  | "UNKNOWN";

export const timelineFilters: Array<{ id: TimelineFilter; label: string }> = [
  { id: "ALL", label: "All" },
  { id: "PROBLEMS", label: "Problems" },
  { id: "WATERING", label: "Watering" },
  { id: "ZONES", label: "Zones" },
  { id: "PROGRAMS", label: "Programs" },
  { id: "TWO_WIRE", label: "Two-Wire" },
  { id: "BASEMANAGER", label: "BaseManager" },
  { id: "FLOW", label: "Flow / POC" },
  { id: "MOISTURE", label: "Moisture" },
  { id: "CONFIGURATION", label: "Configuration" },
  { id: "SYSTEM", label: "System" },
  { id: "UNKNOWN", label: "Unknown" },
];

export function eventMatchesFilter(event: ParsedEvent, filter: TimelineFilter) {
  switch (filter) {
    case "ALL":
      return true;
    case "PROBLEMS":
      return event.severity === "WARNING" || event.severity === "CRITICAL";
    case "WATERING":
      return event.category === "WATERING";
    case "ZONES":
      return event.subjectCode === "ZN" || event.category === "ZONE";
    case "PROGRAMS":
      return event.subjectCode === "PG" || event.category === "PROGRAM";
    case "TWO_WIRE":
      return event.category === "TWO_WIRE";
    case "BASEMANAGER":
      return event.category === "BASEMANAGER" || event.category === "COMMUNICATION";
    case "FLOW":
      return event.category === "FLOW";
    case "MOISTURE":
      return event.category === "MOISTURE";
    case "CONFIGURATION":
      return event.category === "CONFIGURATION";
    case "SYSTEM":
      return event.category === "SYSTEM";
    case "UNKNOWN":
      return event.category === "UNKNOWN";
  }
}

export function eventMatchesSearch(event: ParsedEvent, upload: Upload | undefined, query: string) {
  const search = query.trim().toLowerCase();
  if (!search) return true;
  const haystack = [
    ...timestampSearchTerms(event.timestamp),
    event.title,
    event.summary,
    event.plainEnglish,
    event.subjectCode,
    event.actionCode,
    event.triggerCode,
    event.subjectLabel,
    event.actionLabel,
    event.triggerLabel,
    event.zoneNumber,
    event.programNumber,
    event.pointOfConnection,
    event.masterValve,
    event.deviceSerialNumber,
    event.errorText,
    event.rawRow,
    upload?.controllerName,
    upload?.projectName,
    ...event.detailEntries.flatMap((detail) => [
      detail.code,
      detail.label,
      detail.rawValue,
      ...detail.values,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(search) || normalizeSearchText(haystack).includes(normalizeSearchText(search));
}

export function eventMatchesDateTime(event: ParsedEvent, dateFilter: string, timeFilter: string) {
  const eventDate = event.timestamp.slice(0, 10);
  const eventTime = event.timestamp.slice(11, 16);
  return (!dateFilter || eventDate === dateFilter) && (!timeFilter || eventTime === timeFilter);
}

export function sortEvents(events: ParsedEvent[], direction: "newest" | "oldest") {
  return [...events].sort((a, b) =>
    direction === "newest"
      ? b.timestamp.localeCompare(a.timestamp)
      : a.timestamp.localeCompare(b.timestamp),
  );
}

export function groupEventsByDate(events: ParsedEvent[]) {
  const groups = new Map<string, ParsedEvent[]>();
  events.forEach((event) => {
    const key = event.timestamp.slice(0, 10);
    groups.set(key, [...(groups.get(key) ?? []), event]);
  });
  return [...groups.entries()];
}

export function severityTone(severity: EventSeverity) {
  if (severity === "CRITICAL") return "bg-red-50 text-red-800 ring-red-200";
  if (severity === "WARNING") return "bg-amber-50 text-amber-800 ring-amber-200";
  return "bg-slate-50 text-slate-700 ring-slate-200";
}

export function categoryTone(category: EventCategory) {
  const tones: Record<EventCategory, string> = {
    WATERING: "bg-sky-50 text-sky-800 ring-sky-200",
    PROGRAM: "bg-indigo-50 text-indigo-800 ring-indigo-200",
    ZONE: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    FLOW: "bg-cyan-50 text-cyan-800 ring-cyan-200",
    MOISTURE: "bg-teal-50 text-teal-800 ring-teal-200",
    TWO_WIRE: "bg-red-50 text-red-800 ring-red-200",
    BASEMANAGER: "bg-blue-50 text-blue-800 ring-blue-200",
    SYSTEM: "bg-slate-50 text-slate-800 ring-slate-200",
    CONFIGURATION: "bg-violet-50 text-violet-800 ring-violet-200",
    COMMUNICATION: "bg-blue-50 text-blue-800 ring-blue-200",
    ERROR: "bg-amber-50 text-amber-800 ring-amber-200",
    UNKNOWN: "bg-zinc-50 text-zinc-800 ring-zinc-200",
  };
  return tones[category];
}

function timestampSearchTerms(timestamp: string) {
  const [datePart, timePart = "00:00:00"] = timestamp.split("T");
  const [year, monthText, dayText] = datePart.split("-");
  const [hourText = "00", minuteText = "00"] = timePart.split(":");
  const yearNumber = Number(year);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (![yearNumber, month, day, hour, minute].every(Number.isFinite)) {
    return [timestamp];
  }

  const shortYear = String(yearNumber).slice(-2);
  const hour12 = hour % 12 || 12;
  const suffix = hour >= 12 ? "PM" : "AM";
  const minutePadded = String(minute).padStart(2, "0");
  const dateTerms = [
    datePart,
    `${month}/${day}/${shortYear}`,
    `${month}/${day}/${yearNumber}`,
    `${monthText}/${dayText}/${shortYear}`,
    `${monthText}/${dayText}/${yearNumber}`,
  ];
  const timeTerms = [
    timePart,
    `${hourText}:${minutePadded}`,
    `${hour12}:${minutePadded} ${suffix}`,
    `${hour12}:${minutePadded}${suffix}`,
  ];
  const combinedTerms = dateTerms.flatMap((date) =>
    timeTerms.flatMap((time) => [`${date} ${time}`, `${date} at ${time}`]),
  );

  return [...new Set([timestamp, ...dateTerms, ...timeTerms, ...combinedTerms])];
}

function normalizeSearchText(value: string) {
  return value
    .toLowerCase()
    .replace(/\bat\s+/g, "")
    .replace(/\s+(am|pm)\b/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}
